import type { Entry } from 'contentful'
import groupBy from 'lodash.groupby'
import get from 'lodash/get'

import type { IPageStoriesFields, IPageStoryFields } from 'contentful-shared'
import { Appear } from 'bl-common/src/units/Appear'

import { withTransition } from '../../../utils/withTransition'
import { Metadata } from '../../Metadata'
import Page from '../../Page'
import NewsletterSignupFormSection from '../../TemplatePage/sections/NewsletterSignupFormSection'
import { ExploreTopics } from './ExploreTopics'
import { FeaturedStory } from './FeaturedStory'
import HeaderSection from './HeaderSection'
import { LatestStories } from './LatestStories'
import { RecentFeatures } from './RecentFeatures'
import { StoriesByTopic } from './StoriesByTopic'

const MAX_FEATURED_TOPICS = 2
const MAX_RECENT_STORIES = 4

type StoriesPageProps = {
  page: Entry<IPageStoriesFields>
  stories: Entry<IPageStoryFields>[]
}

const StoriesPage = ({ page, stories }: StoriesPageProps) => {
  const {
    title,
    metadata,
    featuredStory,
    newsletterSignup,
    heading,
    subtitle,
    headerImages,
  } = page.fields
  const recentlyFeaturedStories = stories
    .filter(story => get(story, 'fields.featured', false) === true)
    .slice(0, MAX_RECENT_STORIES)

  const featuredStoryTopics = get(page, 'fields.frontPageTopics', []).slice(
    0,
    MAX_FEATURED_TOPICS
  )

  const [firstFeaturedTopic, secondFeaturedTopic] = featuredStoryTopics

  const storiesByTopics = topic =>
    groupBy(stories, 'fields.topic.fields.topicName')[topic]

  return (
    <Page breadcrumb="Overview">
      <Metadata metadata={metadata} title={title} description={subtitle} />
      <HeaderSection
        heading={heading}
        subtitle={subtitle}
        headerImages={headerImages}
      />
      <Appear observer>
        <LatestStories
          stories={stories}
          bottom={{ xs: 3.5, md: 12 }}
          featuredStory={featuredStory}
          link="/stories/all"
        />
        <FeaturedStory
          story={featuredStory}
          top={{ xs: 3 }}
          bottom={{ xs: 3.5, md: 10 }}
        />
      </Appear>
      {firstFeaturedTopic && (
        <Appear observer>
          <StoriesByTopic
            stories={storiesByTopics(
              get(firstFeaturedTopic, 'fields.topicName')
            )}
            heading={get(firstFeaturedTopic, 'fields.topicName')}
            slug={get(firstFeaturedTopic, 'fields.topicSlug')}
            top={{ xs: 3, md: 8 }}
            bottom={{ xs: 3, md: 12 }}
          />
        </Appear>
      )}
      <RecentFeatures
        stories={recentlyFeaturedStories}
        top={{ xs: 2.5, md: 4 }}
        bottom={{ xs: 2.5, md: 4 }}
      />
      {secondFeaturedTopic && (
        <Appear observer>
          <StoriesByTopic
            stories={storiesByTopics(
              get(secondFeaturedTopic, 'fields.topicName')
            )}
            heading={get(secondFeaturedTopic, 'fields.topicName')}
            slug={get(secondFeaturedTopic, 'fields.topicSlug')}
            top={{ xs: 3, md: 10 }}
            bottom={{ xs: 3.5, md: 6 }}
          />
        </Appear>
      )}
      <Appear observer>
        <ExploreTopics
          heading="Explore other topics"
          description="Discover the waters of the Blue Lagoon. Journey through a spa of the volcanic earth. Harmonize with nature."
          topics={page.fields?.exploreOtherTopics}
        />
      </Appear>
      {newsletterSignup && (
        <NewsletterSignupFormSection section={newsletterSignup} />
      )}
    </Page>
  )
}

export default withTransition(StoriesPage)
